'use client';

import { useMemo } from 'react';
import { RecordingEvent } from '@/types/pulse';
import { Transcript } from '@/types/pulsedb';
import { PlyrMarker } from './usePlyr';

export interface UseTranscriptMarkersOptions {
  /** Transcript containing events */
  transcript: Transcript | null | undefined;
  /** Optional event index to calculate start time from */
  startEventIndex?: number;
}

export interface UseTranscriptMarkersReturn {
  /** Markers generated from transcript topics */
  markers: PlyrMarker[];
  /** Start time in seconds (from startEventIndex) */
  startTime: number;
  /** All events from the transcript */
  events: RecordingEvent[];
}

/**
 * Hook for generating Plyr markers from transcript topics.
 * Also calculates the start time if a startEventIndex is provided.
 */
export function useTranscriptMarkers(
  options: UseTranscriptMarkersOptions
): UseTranscriptMarkersReturn {
  const { transcript, startEventIndex } = options;

  return useMemo(() => {
    if (!transcript) {
      return { markers: [], startTime: 0, events: [] };
    }

    const events = transcript.events as unknown as RecordingEvent[];

    // Calculate start time from event index
    let startTime = 0;
    if (
      startEventIndex !== undefined &&
      startEventIndex >= 0 &&
      startEventIndex < events.length
    ) {
      const event = events[startEventIndex];
      startTime = event.from_start
        ? event.from_start / 1000
        : event.seconds_from_start || 0;
    }

    // Generate topic markers (one per unique topic)
    const seenTopics = new Set<string>();
    const markers: PlyrMarker[] = events
      .filter((event) => event.topic != null)
      .reduce((acc: PlyrMarker[], event) => {
        if (event.topic && !seenTopics.has(event.topic)) {
          const timeInSeconds = event.from_start
            ? event.from_start / 1000
            : event.seconds_from_start || 0;
          acc.push({
            time: Math.floor(timeInSeconds),
            label: event.topic
          });
          seenTopics.add(event.topic);
        }
        return acc;
      }, []);

    return { markers, startTime, events };
  }, [transcript, startEventIndex]);
}
